import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Download, FileImage, FileText, Filter, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => format(new Date(), "yyyy-MM-dd");
const STATUS_DISPLAY = { "Closed Won": "Confirmed", Converted: "Confirmed", "Closed Lost": "Declined", Closed: "Declined", New: "Open" };

const formatDateTime = value => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : format(date, "dd MMM yyyy, hh:mm a");
};
const formatDate = value => value ? format(new Date(`${value}T00:00:00`), "dd MMM yyyy") : "—";
const csvCell = value => `"${String(value ?? "").replace(/"/g, '""')}"`;
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
const cleanPdfText = value => String(value ?? "").normalize("NFKD").replace(/[^\x20-\x7E]/g, "-").replace(/([\\()])/g, "\\$1");

const EXPORT_COLUMNS = [
  { label: "Name", width: 86, value: lead => lead.customer_name },
  { label: "Phone", width: 62, value: lead => lead.phone },
  { label: "RM", width: 78, value: lead => lead.assigned_to_name || "Unassigned" },
  { label: "Route", width: 120, value: lead => `${lead.moving_from || "-"} to ${lead.moving_to || "-"}` },
  { label: "Added", width: 78, value: lead => formatDateTime(lead.created_at) },
  { label: "Assigned", width: 78, value: lead => formatDateTime(lead.assigned_at) },
  { label: "Next Follow Up / Action", width: 95, value: lead => `${formatDate(lead.next_followup_date)} | ${lead.next_followup_action || "-"}` },
  { label: "Status", width: 60, value: lead => STATUS_DISPLAY[lead.status] || lead.status },
  { label: "Shifting", width: 60, value: lead => formatDate(lead.shifting_date) },
];

const cellLines = (value, width, maxLines = 2) => {
  const text = String(value ?? "-");
  const chars = Math.max(8, Math.floor(width / 3.7));
  const lines = [];
  let remaining = text;
  while (remaining && lines.length < maxLines) {
    if (remaining.length <= chars) { lines.push(remaining); remaining = ""; break; }
    let cut = remaining.lastIndexOf(" ", chars);
    if (cut < Math.floor(chars * 0.55)) cut = chars;
    lines.push(remaining.slice(0, cut).trim());
    remaining = remaining.slice(cut).trim();
  }
  if (remaining && lines.length) lines[lines.length - 1] = `${lines[lines.length - 1].slice(0, -3)}...`;
  return lines.length ? lines : ["-"];
};

function createTablePdf({ selectedRM, applied, metrics, leads }) {
  const pageRows = [];
  for (let index = 0; index < leads.length; index += 16) pageRows.push(leads.slice(index, index + 16));
  if (pageRows.length === 0) pageRows.push([]);
  const objects = [null];
  const add = value => { objects.push(value); return objects.length - 1; };
  const fontId = add("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  const boldFontId = add("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>");
  const pagesId = add("");
  const pageIds = pageRows.map((rows, pageIndex) => {
    const commands = [
      "0.106 0.165 0.42 rg",
      `BT /F2 18 Tf 30 555 Td (Daily Summary) Tj ET`,
      "0.28 0.33 0.41 rg",
      `BT /F1 8 Tf 30 537 Td (RM: ${cleanPdfText(selectedRM)}  |  Period: ${cleanPdfText(applied.fromDate)} to ${cleanPdfText(applied.toDate)}) Tj ET`,
      `BT /F2 9 Tf 30 516 Td (Today's Assigned: ${metrics.today_assigned || 0}     Today's Updated: ${metrics.today_updated || 0}     Exported Leads: ${leads.length}) Tj ET`,
    ];
    let x = 30;
    const headerTop = 493;
    EXPORT_COLUMNS.forEach(column => {
      commands.push("0.106 0.165 0.42 rg", `${x} ${headerTop - 30} ${column.width} 30 re f`, "1 1 1 rg");
      cellLines(column.label, column.width).forEach((line, lineIndex) => commands.push(`BT /F2 6 Tf ${x + 3} ${headerTop - 12 - lineIndex * 8} Td (${cleanPdfText(line)}) Tj ET`));
      x += column.width;
    });
    rows.forEach((lead, rowIndex) => {
      const top = headerTop - 30 - rowIndex * 25;
      x = 30;
      EXPORT_COLUMNS.forEach(column => {
        commands.push(rowIndex % 2 ? "0.97 0.98 1 rg" : "1 1 1 rg", `${x} ${top - 25} ${column.width} 25 re f`, "0.82 0.85 0.89 RG", `${x} ${top - 25} ${column.width} 25 re S`, "0.06 0.09 0.16 rg");
        cellLines(column.value(lead), column.width).forEach((line, lineIndex) => commands.push(`BT /F1 5.5 Tf ${x + 3} ${top - 10 - lineIndex * 8} Td (${cleanPdfText(line)}) Tj ET`));
        x += column.width;
      });
    });
    commands.push("0.28 0.33 0.41 rg", `BT /F1 7 Tf 760 20 Td (Page ${pageIndex + 1} of ${pageRows.length}) Tj ET`);
    const stream = commands.join("\n");
    const contentId = add(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    return add(`<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 842 595] /Resources << /Font << /F1 ${fontId} 0 R /F2 ${boldFontId} 0 R >> >> /Contents ${contentId} 0 R >>`);
  });
  objects[pagesId] = `<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;
  const catalogId = add(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);
  let output = "%PDF-1.4\n";
  const offsets = [0];
  objects.slice(1).forEach((object, index) => {
    offsets[index + 1] = output.length;
    output += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xref = output.length;
  output += `xref\n0 ${objects.length}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach(offset => { output += `${String(offset).padStart(10, "0")} 00000 n \n`; });
  output += `trailer\n<< /Size ${objects.length} /Root ${catalogId} 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([output], { type: "application/pdf" });
}

const metricGroups = [
  [
    ["Total Assigned", "total_assigned"], ["Total Updated", "total_updated"], ["Total Confirmed", "total_confirmed"],
    ["Total Declined", "total_declined"], ["Total Invalid", "total_invalid"],
  ],
  [
    ["Today's Assigned", "today_assigned"], ["Today's Updated", "today_updated"], ["Today's Confirmed", "today_confirmed"],
    ["Today's Declined", "today_declined"], ["Today's Invalid", "today_invalid"],
  ],
];

export default function DailySummaryPage() {
  const navigate = useNavigate();
  const [relationshipManagers, setRelationshipManagers] = useState([]);
  const [filters, setFilters] = useState({ rmId: "all", fromDate: today(), toDate: today() });
  const [applied, setApplied] = useState(filters);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const selectedRM = useMemo(() => relationshipManagers.find(rm => rm.id === applied.rmId)?.name || "All Relationship Managers", [relationshipManagers, applied.rmId]);
  const exportLeads = useMemo(() => {
    const exportIds = new Set(summary?.export_lead_ids || []);
    return (summary?.leads || []).filter(lead => exportIds.has(lead.id));
  }, [summary]);

  const loadSummary = async next => {
    if (next.fromDate > next.toDate) { toast.error("From Date cannot be after To Date."); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reporting/daily-summary`, {
        params: { rm_id: next.rmId, from_date: next.fromDate, to_date: next.toDate },
        withCredentials: true,
      });
      setSummary(data);
      setApplied(next);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load daily summary.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/users/relationship-managers`, { withCredentials: true })
      .then(response => setRelationshipManagers(response.data))
      .catch(() => toast.error("Failed to load Relationship Managers."));
    loadSummary({ rmId: "all", fromDate: today(), toDate: today() });
  }, []);

  const exportCsv = () => {
    const metrics = summary.metrics;
    const rows = [
      ["Daily Summary"], ["Relationship Manager", selectedRM], ["From Date", applied.fromDate], ["To Date", applied.toDate], [],
      ["Metric", "Value"], ["Today's Assigned", metrics.today_assigned || 0], ["Today's Updated", metrics.today_updated || 0], ["Exported Leads", exportLeads.length], [],
      ["Name", "Phone", "RM", "Route", "Added Date and Time", "Assigned Date and Time", "Next Follow Up Date", "Action Item", "Status", "Shifting Date"],
      ...exportLeads.map(lead => [lead.customer_name, lead.phone, lead.assigned_to_name || "Unassigned", `${lead.moving_from || ""} → ${lead.moving_to || ""}`, formatDateTime(lead.created_at), formatDateTime(lead.assigned_at), formatDate(lead.next_followup_date), lead.next_followup_action || "", STATUS_DISPLAY[lead.status] || lead.status, formatDate(lead.shifting_date)]),
    ];
    downloadBlob(new Blob([rows.map(row => row.map(csvCell).join(",")).join("\n")], { type: "text/csv;charset=utf-8" }), `daily-summary-${applied.fromDate}-${applied.toDate}.csv`);
  };

  const exportPdf = () => downloadBlob(createTablePdf({ selectedRM, applied, metrics: summary.metrics, leads: exportLeads }), `daily-summary-${applied.fromDate}-${applied.toDate}.pdf`);

  const exportImage = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1800;
    const scale = (canvas.width - 80) / EXPORT_COLUMNS.reduce((total, column) => total + column.width, 0);
    const rowHeight = 58;
    canvas.height = Math.max(500, 210 + (exportLeads.length + 1) * rowHeight);
    const context = canvas.getContext("2d");
    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#1B2A6B";
    context.font = "bold 34px Arial";
    context.fillText("Daily Summary", 40, 50);
    context.fillStyle = "#475569";
    context.font = "18px Arial";
    context.fillText(`RM: ${selectedRM}   |   Period: ${applied.fromDate} to ${applied.toDate}`, 40, 82);
    context.fillStyle = "#1B2A6B";
    context.font = "bold 20px Arial";
    context.fillText(`Today's Assigned: ${summary.metrics.today_assigned || 0}     Today's Updated: ${summary.metrics.today_updated || 0}     Exported Leads: ${exportLeads.length}`, 40, 118);

    const tableTop = 150;
    let x = 40;
    context.textBaseline = "top";
    EXPORT_COLUMNS.forEach(column => {
      const width = column.width * scale;
      context.fillStyle = "#1B2A6B";
      context.fillRect(x, tableTop, width, rowHeight);
      context.fillStyle = "#FFFFFF";
      context.font = "bold 15px Arial";
      cellLines(column.label, column.width).forEach((line, lineIndex) => context.fillText(line, x + 8, tableTop + 11 + lineIndex * 18));
      x += width;
    });
    exportLeads.forEach((lead, rowIndex) => {
      x = 40;
      const y = tableTop + rowHeight * (rowIndex + 1);
      EXPORT_COLUMNS.forEach(column => {
        const width = column.width * scale;
        context.fillStyle = rowIndex % 2 ? "#F8FAFC" : "#FFFFFF";
        context.fillRect(x, y, width, rowHeight);
        context.strokeStyle = "#CBD5E1";
        context.strokeRect(x, y, width, rowHeight);
        context.fillStyle = "#0F172A";
        context.font = "14px Arial";
        cellLines(column.value(lead), column.width).forEach((line, lineIndex) => context.fillText(line, x + 8, y + 10 + lineIndex * 18));
        x += width;
      });
    });
    canvas.toBlob(blob => {
      if (blob) downloadBlob(blob, `daily-summary-${applied.fromDate}-${applied.toDate}.png`);
    }, "image/png");
  };

  const metrics = summary?.metrics || {};
  return (
    <div className="space-y-5" data-testid="daily-summary-page">
      <div>
        <h2 className="text-xl font-bold text-[#1B2A6B]">Daily Summary</h2>
        <p className="mt-0.5 text-xs text-[#475569]">Review RM lead totals and activity for a selected period</p>
      </div>

      <div className="border border-[#E2E8F0] bg-white p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_auto] lg:items-end">
          <label className="text-[11px] font-bold uppercase tracking-widest text-[#1B2A6B]">Relationship Manager
            <select value={filters.rmId} onChange={event => setFilters(current => ({ ...current, rmId: event.target.value }))} className="mt-1.5 block h-10 w-full border border-[#E2E8F0] bg-white px-3 text-sm font-normal normal-case tracking-normal text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none">
              <option value="all">All Relationship Managers</option>
              {relationshipManagers.map(rm => <option key={rm.id} value={rm.id}>{rm.name}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-bold uppercase tracking-widest text-[#1B2A6B]">From Date
            <input type="date" value={filters.fromDate} onChange={event => setFilters(current => ({ ...current, fromDate: event.target.value }))} className="mt-1.5 block h-10 w-full border border-[#E2E8F0] px-3 text-sm font-normal normal-case tracking-normal focus:border-[#1B2A6B] focus:outline-none" />
          </label>
          <label className="text-[11px] font-bold uppercase tracking-widest text-[#1B2A6B]">To Date
            <input type="date" value={filters.toDate} onChange={event => setFilters(current => ({ ...current, toDate: event.target.value }))} className="mt-1.5 block h-10 w-full border border-[#E2E8F0] px-3 text-sm font-normal normal-case tracking-normal focus:border-[#1B2A6B] focus:outline-none" />
          </label>
          <Button onClick={() => loadSummary({ ...filters })} className="h-10 rounded-none border-b-2 border-[#FFD700] bg-[#1B2A6B] px-5 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#152257]"><Filter size={14} className="mr-1.5" />Filter</Button>
        </div>
      </div>

      {loading ? <div className="border border-[#E2E8F0] bg-white py-20 text-center text-sm text-[#94A3B8]">Loading summary...</div> : summary && <>
        <div className="flex flex-wrap gap-3">
          <Button onClick={exportCsv} className="h-10 rounded-none bg-[#1B2A6B] text-xs font-bold uppercase tracking-widest text-white hover:bg-[#152257]"><Download size={14} className="mr-2" />Export as CSV</Button>
          <Button onClick={exportPdf} className="h-10 rounded-none bg-[#1B2A6B] text-xs font-bold uppercase tracking-widest text-white hover:bg-[#152257]"><FileText size={14} className="mr-2" />Export as PDF</Button>
          <Button onClick={exportImage} className="h-10 rounded-none bg-[#1B2A6B] text-xs font-bold uppercase tracking-widest text-white hover:bg-[#152257]"><FileImage size={14} className="mr-2" />Export as Image</Button>
        </div>

        {metricGroups.map((group, groupIndex) => <div key={groupIndex} className={`grid grid-cols-2 gap-3 ${group.length === 5 ? "xl:grid-cols-5" : "lg:grid-cols-4"}`}>
          {group.map(([label, key]) => <div key={key} className="border border-[#E2E8F0] border-t-4 border-t-[#1B2A6B] bg-white p-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">{label}</p>
            <p className="mt-2 text-3xl font-black text-[#1B2A6B]">{metrics[key] || 0}</p>
          </div>)}
        </div>)}

        <div className="border border-[#E2E8F0] bg-white">
          <div className="flex items-center justify-between border-b border-[#E2E8F0] px-4 py-3">
            <div><h3 className="font-bold text-[#1B2A6B]">Assigned Leads</h3><p className="text-xs text-[#64748B]">{summary.leads.length} lead{summary.leads.length === 1 ? "" : "s"}</p></div>
          </div>
          {summary.leads.length === 0 ? <div className="flex flex-col items-center gap-2 py-16 text-sm text-[#94A3B8]"><Users size={34} className="text-[#CBD5E1]" />No assigned leads found.</div> : <div className="overflow-x-auto">
            <table className="w-full min-w-[1250px] text-sm">
              <thead><tr className="border-b border-[#E2E8F0] bg-[#F9FAFB]">
                {["Name", "RM", "Route", "Added Date\nand Time", "Assigned Date\nand Time", "Next Follow Up Date", "Status", "Shifting Date"].map(label => <th key={label} className="whitespace-pre-line px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-[#475569]">{label}</th>)}
              </tr></thead>
              <tbody>{summary.leads.map((lead, index) => <tr key={lead.id} onClick={() => navigate(`/leads/${lead.id}`)} className={`${index % 2 ? "bg-[#F8FAFC]" : "bg-white"} cursor-pointer border-b border-[#E2E8F0] hover:bg-[#EEF2FF]`}>
                <td className="whitespace-nowrap px-4 py-3"><p className="font-semibold text-[#1B2A6B]">{lead.customer_name}</p><p className="mt-0.5 text-[10px] text-[#64748B]">{lead.phone || "—"}</p></td>
                <td className="whitespace-nowrap px-4 py-3 font-medium text-[#1B2A6B]">{lead.assigned_to_name || "Unassigned"}</td>
                <td className="max-w-[250px] px-4 py-3"><span className="block truncate" title={`${lead.moving_from || "—"} → ${lead.moving_to || "—"}`}>{lead.moving_from || "—"} → {lead.moving_to || "—"}</span></td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-[#475569]">{formatDateTime(lead.created_at)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-[#475569]">{formatDateTime(lead.assigned_at)}</td>
                <td className="px-4 py-3"><p className="whitespace-nowrap text-xs">{formatDate(lead.next_followup_date)}</p><p className="mt-0.5 max-w-[180px] truncate text-[10px] text-[#64748B]" title={lead.next_followup_action}>{lead.next_followup_action || "—"}</p></td>
                <td className="whitespace-nowrap px-4 py-3 text-xs font-bold text-[#1B2A6B]">{STATUS_DISPLAY[lead.status] || lead.status}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-[#475569]">{formatDate(lead.shifting_date)}</td>
              </tr>)}</tbody>
            </table>
          </div>}
        </div>

      </>}
    </div>
  );
}
